if (!customElements.get("tj-dot-slider")) {
  class TjDotSlider extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector("[data-tj-viewport]");
      this.track = this.querySelector("[data-tj-track]");
      this.dotsRoot = this.querySelector("[data-tj-dots]");
      if (!this.viewport || !this.track) return;

      this.slides = Array.from(this.track.children);
      this.page = 0;
      this.gap = Number(this.dataset.gap) || 0;
      this.slideW = 0;
      this.pages = 1;

      this.onDotClick = (event) => {
        const button = event.target.closest("[data-tj-page]");
        if (!button) return;
        this.goTo(Number(button.dataset.tjPage));
      };
      this.dotsRoot?.addEventListener("click", this.onDotClick);

      this.drag = { tracking: false, dragging: false, startX: 0, lastX: 0, pointerId: null };
      this.onPointerDown = this.handlePointerDown.bind(this);
      this.onPointerMove = this.handlePointerMove.bind(this);
      this.onPointerUp = this.handlePointerUp.bind(this);
      this.viewport.addEventListener("pointerdown", this.onPointerDown);
      this.viewport.addEventListener("pointermove", this.onPointerMove);
      this.viewport.addEventListener("pointerup", this.onPointerUp);
      this.viewport.addEventListener("pointercancel", this.onPointerUp);

      this.track.addEventListener("click", (event) => {
        if (this.ignoreClick) {
          event.preventDefault();
          event.stopPropagation();
          this.ignoreClick = false;
        }
      }, true);

      this.resizeObserver = new ResizeObserver(() => this.layout());
      this.resizeObserver.observe(this.viewport);
      this.mutationObserver = new MutationObserver(() => {
        this.slides = Array.from(this.track.children);
        this.layout();
      });
      this.mutationObserver.observe(this.track, { childList: true });
      this.layout();
    }

    disconnectedCallback() {
      this.dotsRoot?.removeEventListener("click", this.onDotClick);
      this.viewport?.removeEventListener("pointerdown", this.onPointerDown);
      this.viewport?.removeEventListener("pointermove", this.onPointerMove);
      this.viewport?.removeEventListener("pointerup", this.onPointerUp);
      this.viewport?.removeEventListener("pointercancel", this.onPointerUp);
      this.resizeObserver?.disconnect();
      this.mutationObserver?.disconnect();
    }

    perView() {
      const width = this.viewport.clientWidth;
      const desktop = Number(this.dataset.perDesktop) || 3;
      if (width < 750) return 1;
      if (width < 1100) return Math.min(2, desktop);
      return desktop;
    }

    layout() {
      const perView = this.perView();
      const viewportWidth = this.viewport.clientWidth;
      const slideWidth = (viewportWidth - this.gap * (perView - 1)) / perView;

      this.slideW = slideWidth;
      this.pages = Math.max(1, Math.ceil(this.slides.length / perView));
      this.page = Math.min(this.page, this.pages - 1);

      this.slides.forEach((slide) => {
        slide.style.flex = `0 0 ${slideWidth}px`;
        slide.style.width = `${slideWidth}px`;
        slide.style.maxWidth = `${slideWidth}px`;
      });

      this.track.style.gap = `${this.gap}px`;
      this.renderDots();
      this.update(false);
    }

    renderDots() {
      if (!this.dotsRoot) return;
      if (this.pages <= 1) {
        this.dotsRoot.hidden = true;
        this.dotsRoot.innerHTML = "";
        return;
      }

      this.dotsRoot.hidden = false;
      const buttons = [];
      for (let i = 0; i < this.pages; i += 1) {
        const active = i === this.page;
        buttons.push(
          `<button type="button"${active ? ' class="is-active"' : ""} data-tj-page="${i}" aria-label="Reviews page ${i + 1}" aria-current="${active ? "true" : "false"}"></button>`
        );
      }
      this.dotsRoot.innerHTML = buttons.join("");
    }

    goTo(page) {
      if (this.pages <= 1) return;
      this.page = (page + this.pages) % this.pages;
      this.update(true);
      this.syncDots();
    }

    startIndex() {
      return this.page * this.perView();
    }

    offset() {
      return this.startIndex() * (this.slideW + this.gap);
    }

    update(animate) {
      this.track.style.transition = animate === false ? "none" : "transform 0.45s ease";
      this.track.style.transform = `translate3d(-${this.offset()}px, 0, 0)`;
    }

    syncDots() {
      if (!this.dotsRoot) return;
      this.dotsRoot.querySelectorAll("[data-tj-page]").forEach((button) => {
        const active = Number(button.dataset.tjPage) === this.page;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-current", active ? "true" : "false");
      });
    }

    handlePointerDown(event) {
      if (this.pages <= 1) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      this.drag.tracking = true;
      this.drag.dragging = false;
      this.drag.startX = event.clientX;
      this.drag.lastX = event.clientX;
      this.drag.pointerId = event.pointerId;
    }

    handlePointerMove(event) {
      if (!this.drag.tracking) return;
      this.drag.lastX = event.clientX;
      const delta = event.clientX - this.drag.startX;
      if (!this.drag.dragging) {
        if (Math.abs(delta) < 12) return;
        this.drag.dragging = true;
        this.track.style.transition = "none";
        this.viewport.setPointerCapture?.(event.pointerId);
      }
      this.track.style.transform = `translate3d(${delta - this.offset()}px, 0, 0)`;
    }

    handlePointerUp() {
      if (!this.drag.tracking) return;
      const wasDragging = this.drag.dragging;
      const delta = this.drag.lastX - this.drag.startX;
      const pointerId = this.drag.pointerId;
      this.drag.tracking = false;
      this.drag.dragging = false;
      this.drag.pointerId = null;
      if (pointerId != null && this.viewport.hasPointerCapture?.(pointerId)) {
        this.viewport.releasePointerCapture(pointerId);
      }
      if (!wasDragging) return;
      this.ignoreClick = true;
      if (Math.abs(delta) > 50) this.goTo(this.page + (delta < 0 ? 1 : -1));
      else this.update(true);
    }
  }

  customElements.define("tj-dot-slider", TjDotSlider);
}
